import {
  Component,
  OnInit,
  signal
} from '@angular/core';

import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors
} from '@angular/forms';

import {
  ActivatedRoute,
  Router
} from '@angular/router';

import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';

import {
  AuthService
} from '../../api-authorization/authorize.service';


@Component({
  selector: 'app-user-register',
  templateUrl: './user-register.component.html',
  styleUrls: ['./user-register.component.css']
})
export class UserRegisterComponent implements OnInit {

  changePasswordForm: FormGroup;

  isLoading = false;

  error: string | null = null;

  hideOldPassword = signal(true);

  hideNewPassword = signal(true);

  hideConfirmPassword = signal(true);


  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute
  ) {

    this.changePasswordForm =
      this.fb.nonNullable.group(
        {
          oldPassword: [
            '',
            Validators.required
          ],

          newPassword: [
            '',
            [
              Validators.required,
              Validators.minLength(8)
            ]
          ],

          confirmPassword: [
            '',
            Validators.required
          ]
        },
        {
          validators:
            this.passwordMatchValidator
        }
      );
  }


  ngOnInit(): void {

    this.changePasswordForm
      .valueChanges
      .subscribe(() => {

        this.error = null;
      });
  }


  // ========================================================
  // PASSWORT-VALIDIERUNG
  // ========================================================

  passwordMatchValidator(
    group: AbstractControl
  ): ValidationErrors | null {

    const newPassword =
      group.get('newPassword')?.value;

    const confirmPassword =
      group.get('confirmPassword')?.value;

    const confirmControl =
      group.get('confirmPassword');


    if (!confirmControl) {
      return null;
    }


    /*
     * Bestehende Fehler behalten,
     * aber unseren eigenen not_matching-Fehler
     * sauber entfernen bzw. hinzufügen.
     */
    const errors = {
      ...(confirmControl.errors ?? {})
    };


    delete errors['not_matching'];


    if (
      compare(
        newPassword,
        confirmPassword
      )
    ) {

      errors['not_matching'] = true;
    }


    confirmControl.setErrors(
      Object.keys(errors).length > 0
        ? errors
        : null
    );


    return null;
  }


  toggleVisibility(
    signalToToggle: any
  ): void {

    signalToToggle.set(
      !signalToToggle()
    );
  }


  // ========================================================
  // FEHLERMELDUNG
  // ========================================================

  private readError(
    error: any
  ): string {

    const msg =
      error?.error;


    if (
      typeof msg === 'string'
      &&
      msg.trim()
    ) {

      return msg;
    }


    if (msg?.message) {

      return msg.message;
    }


    return (
      'Fehler beim Ändern des Passworts. '
      +
      'Bitte überprüfen Sie Ihre Eingaben.'
    );
  }


  // ========================================================
  // PASSWORT ÄNDERN
  // ========================================================

  onSubmit(): void {

    this.error = null;


    if (this.changePasswordForm.invalid) {

      this.changePasswordForm
        .markAllAsTouched();

      return;
    }


    this.isLoading = true;


    const {
      oldPassword,
      newPassword
    } =
      this.changePasswordForm.value;


    this.authService
      .changePassword(
        oldPassword,
        newPassword
      )
      .pipe(

        catchError(error => {

          this.error =
            this.readError(error);

          this.isLoading = false;

          return of(null);
        })

      )
      .subscribe({

        next: result => {

          if (result === null) {
            return;
          }


          this.isLoading = false;


          /*
           * Wenn der Benutzer über einen QR-Code
           * gekommen ist:
           *
           * /changepassword?returnUrl=/q/m/ABC
           *
           * geht es nach der Änderung wieder genau
           * zu diesem QR-Code zurück.
           *
           * Ohne returnUrl bleibt das bisherige
           * Verhalten bestehen und /users wird geöffnet.
           */
          this.router.navigateByUrl(
            this.getReturnUrl()
          );
        },


        error: error => {

          this.error =
            this.readError(error);

          this.isLoading = false;
        }

      });
  }


  // ========================================================
  // RÜCKSPRUNG
  // ========================================================

  private getReturnUrl(): string {

    const returnUrl =
      this.route.snapshot
        .queryParamMap
        .get('returnUrl');


    /*
     * Nur interne URLs zulassen.
     */
    if (
      returnUrl
      &&
      returnUrl.startsWith('/')
      &&
      !returnUrl.startsWith('//')
    ) {

      return returnUrl;
    }


    /*
     * Bisheriges Standardverhalten.
     */
    return '/users';
  }
}


function compare(
  password: string,
  confirmPassword: string
): boolean {

  return (
    password !== confirmPassword
    &&
    confirmPassword !== ''
  );
}